import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer } from 'recharts';

// import { chartTimePeriod } from '../../../config/enum';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import CustomDateRangePicker from '../../../../shared-components/custom-date-range/CustomDateRangePicker';
// import CustomToggleButton from '../../../shared-components/custom-toggle-button/CustomToggleButton';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../../shared-functions/ChartFunctions';
import stationManagement from '../../../../utils/api/station-management';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

interface ChartDataProperties {
  label: string;
  [key: string]: string | number;
}

/**
 * Revenue Report Chart
 * @returns {any} The rendered component
 */
function RevenueReportChart() {
  const { t } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });

  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);

  const primaryColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--primary-color')
    .trim();

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#1F2937',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        padding: 10,
        borderRadius: 8,
        displayColors: false,
        callbacks: {
          /**
           * Returns the tooltip title.
           * @returns {string} The tooltip title string.
           */
          title: () => `${t('revenue')} (${t('sar')})`,
          /**
           * Formats the tooltip label using locale string.
           * @param {any} context - The tooltip context object.
           * @returns {string} The formatted label string.
           */
          label: (context: any) => `${context.raw.toLocaleString()} ${t('sar')}`,
        },
      },
    },
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
      },
      y: {
        grid: { display: false },
        ticks: { display: false },
        border: { display: false },
      },
    },
  };

  /** Draws the vertical crosshair line using --success theme color on hover */
  const verticalHoverLinePlugin = {
    id: 'verticalHoverLine',
    /**
     * Draws a colored vertical crosshair line at the hovered x position.
     * @param {any} chart - The Chart.js instance
     */
    afterDraw(chart: any) {
      if (chart.tooltip?._active?.length) {
        const context = chart.ctx as CanvasRenderingContext2D;
        const { x } = chart.tooltip._active[0].element;
        const { top, bottom } = chart.chartArea;

        const successColor = getComputedStyle(document.documentElement)
          .getPropertyValue('--success')
          .trim();

        context.save();
        context.beginPath();
        context.moveTo(x, top);
        context.lineTo(x, bottom);
        context.lineWidth = 1;
        context.strokeStyle = successColor;
        context.stroke();
        context.restore();
      }
    },
  };

  /**
   * Fetches the power consumed report data for the specified time range and updates the chart data.
   * @param {any} range - The time range for the report ('last24hours', 'last7days', 'lastMonth', 'last12months').
   */
  /**
   * Fetch power consumed data based on time range
   * @param {'last24hours' | 'last7days' | 'lastMonth' | 'last12months'} range The time period range
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    setLoading(true);
    try {
      const response = await stationManagement.fetchRevenueChart({
        timePeriod: range,
        type: 'revenue',
        chargingStation: selectedChargingPoint?.id,
      });
      if (range === 'last12months' && response.status === 200) {
        setBarGraphData(fillMissingMonthsGeneric(response.data));
      } else if (range === 'lastMonth' && response.status === 200) {
        setBarGraphData(fillMissingLastNDaysGeneric(response.data, 30));
      } else if (range === 'last7days' && response.status === 200) {
        setBarGraphData(fillMissingLastNDaysGeneric(response.data, 7));
      } else if (range === 'last24hours' && response.status === 200) {
        setBarGraphData(fillMissingLast24HrsGeneric(response.data));
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  useEffect(() => {
    fetchPowerConsumedReport('last24hours');
  }, []);

  const labels = barGraphData.map((d) => d.label);
  const values = barGraphData.map((d) => d.total || 0);

  return (
    <div className="report-revenue-chart-wrap">
      <div className="report-revenue-container">
        <div className="report-revenue-header">
          <div className="report-revenue-title"> {t('revenue')}</div>
          <CustomDateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={(start, end) => {
              setDateRange({ startDate: start, endDate: end });
              // Trigger fetch with default range; you can adapt to use dates later
              fetchPowerConsumedReport('last24hours');
            }}
            placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          />
        </div>
        <div className="report-revenue-chart-container">
          <div className="report-revenue-chart">
            {loading ? (
              <div className="loader">
                <BatteryLoader />
              </div>
            ) : (
              <ResponsiveContainer height={250}>
                <Line
                  data={{
                    labels,
                    datasets: [
                      {
                        label: t('powerConsumed'),
                        data: values,
                        borderColor: primaryColor,
                        backgroundColor: primaryColor,
                        fill: false,
                        tension: 0.4,
                        borderWidth: 2,
                        pointRadius: 6,
                        pointHoverRadius: 8,
                        pointBackgroundColor: primaryColor,
                        pointBorderColor: primaryColor,
                      },
                    ],
                  }}
                  options={chartOptions}
                  plugins={[verticalHoverLinePlugin]}
                />
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RevenueReportChart;
